import { API_QUERY_PARAMS } from '../../../polly-manage.constants';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { PollyManageBackendService } from '../polly-manage-backend-service/polly-manage-backend.service';
import { saveAs } from 'file-saver';
import { SignedUrlResponse, PublicShareResponse, ElasticSortingQuery, StringToStringObjectMapping, ProcessedDataQueryResponse } from '../../interfaces';
import { map, switchMap, tap } from 'rxjs/operators';
import { DatasetDownloadResponse } from '../../../modules/omixatlas-module/@core/interfaces';
import { PollyOmixatlasDataService } from '../../../modules/omixatlas-module/services';
import { environment } from '../../../../../../environments/environment';
import { WebSocketService, PlotlyService, CommonApiService, PollyMessageService } from 'polly-commons';
import { getDownloadQuery, getElasticQueryDownloadPayload } from '../../utilities/helper-functions/elastic-query-download.utilities';
import { QueryDownloadFileResponse } from '../../interfaces/apis.interface';
import { PlotlyImageFormat } from '../../../modules/shared-module/interfaces';
@Injectable({
  providedIn: 'root',
})
export class PollyManageFileDownloadService {
  private readonly messageService = inject(PollyMessageService);
  constructor(
    private readonly backendService: PollyManageBackendService,
    private readonly http: HttpClient,
    private readonly dataService: PollyOmixatlasDataService,
    private readonly websocketService: WebSocketService,
    private readonly plotlyService: PlotlyService,
    private readonly commonApiService: CommonApiService,
  ) {}

  /**
   * This function is used to download workspace file
   * @param url URL of the resource
   * @param fileName name of the file to be downloaded
   * @param browserBehaviour for downloading files based on the size (whether it should be downloaded as stream)
   */
  public downloadWorkspaceFile(url: string, fileName: string, browserBehaviour: boolean = false): Observable<object> {
    const downloadUrl: string = `${url}?action=${API_QUERY_PARAMS.SINGLE_FILE_DOWNLOAD_ACTION_VALUE}`;
    const downloadFile$ = this.backendService.commonApiGetReq<SignedUrlResponse>(downloadUrl);
    if (browserBehaviour) {
      return downloadFile$.pipe(
        tap((res: SignedUrlResponse) => {
          saveAs(res.data.links.signed_url, fileName);
        })
      );
    } else {
      return downloadFile$.pipe(
        switchMap((res: SignedUrlResponse) => {
          return this.downloadStream(res.data.links.signed_url, fileName);
        })
      );
    }
  }
  /**
   * This function is used to download notebook files for GitHub polly integration
   * @param url URL of the resource
   * @param fileName name of the file to be download
   * @param machine Machine size
   * @param kernel Notebook environment
   *
   * @returns Observable<SignedUrlResponse>
   */
  public downloadForGithub(url: string, fileName: string, machine: string, kernel: string): Observable<SignedUrlResponse> {
    const downloadUrl: string = `${url}?action=${API_QUERY_PARAMS.SINGLE_FILE_DOWNLOAD_ACTION_VALUE}`;
    return this.backendService.commonApiGetReq<SignedUrlResponse>(downloadUrl).pipe(
      switchMap((res: SignedUrlResponse) => {
        return this.addPollyLinkToNotebook(res.data.links.signed_url, fileName, machine, kernel);
      })
    );
  }

  /**
   * @description Notebook type file contains self link of /notebooks/ path, which can not be used for downloading a notebook file.
   * -> We need to replace /notebooks/ with /files/ for API call to download the notebook.
   * -> This function replaces first occurrence of:
   *        /workspaces using regex with /projects in self link.
   *        /notebooks/ using regex with /files/ in self link.
   *
   * @param notebookSelfUrl Self link url of notebook file
   */
  public getNotebookDownloadSelfLink(notebookSelfUrl: string): string {
    // [TODO]: This needs to be removed after getting the proper self link from the backend for this.
    return notebookSelfUrl.replace('/workspaces', '/projects').replace('/notebooks', '/files');
  }

  /**
   * This function is used to download stream of data.
   */
  public downloadStream(url: string, fileName: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      tap((response: Blob) => {
        saveAs(response, fileName);
      })
    );
  }
  /**
   * This function is used to add "open in polly" link to first cell of notebook
   * @param url URL of the resource
   * @param fileName name of the file to be download
   * @param machine Machine size
   * @param kernel Notebook environment
   *
   * @returns Observable<any>
   */
  public addPollyLinkToNotebook(url: string, fileName: string, machine: string, kernel: string): Observable<any> {
    return this.http.get(url).pipe(
      tap((res: { cells?: object[] }) => {
        const fileData: { cells?: object[] } = res;
        const finalUrl = `${environment.baseUrl}/manage/workspaces?action=open_polly_notebook&source=github&path=path_place_holder&kernel=${kernel}&machine=${machine}`;
        // TODO : Rajeev | Check the string escape character requirements below
        const baseHtml = `<a href=\"${finalUrl}\" target=\"_parent\"><img src=\"https://elucidatainc.github.io/PublicAssets/open_polly.svg\" alt=\"Open in Polly\"/></a>\n`;
        const baseJson = {
          cell_type: 'markdown',
          metadata: {
            colab_type: 'text',
            id: 'view-in-github'
          },
          source: [baseHtml]
        };
        if (fileData.cells) { fileData.cells.unshift(baseJson); }
        const str = JSON.stringify(fileData, null, 2);
        const bytes = new TextEncoder().encode(str);
        const blob = new Blob([bytes], {
          type: 'application/json;charset=utf-8'
        });
        saveAs(blob, fileName);
      })
    );
  }

  /**
   * @description Get the shared it and if shared id available it would be a public link otherwise private
   *
   * @param url link.self url from api response
   * @return Observable<string | null>
   */
  public getSharedId(url: string): Observable<string | null> {
    const downloadUrl: string = `${url}?action=${API_QUERY_PARAMS.SINGLE_FILE_DOWNLOAD_ACTION_VALUE}`;
    return this.backendService.commonApiGetReq<SignedUrlResponse>(downloadUrl).pipe(
      map(res => {
        if (res.data.shared_id) {
          return res.data.shared_id;
        }
        return null;
      })
    );
  }

  /**
   * @description Get the link from which we can get direct link of file stored in bucket to preview it
   *
   * @param url link.self url from api response
   * @return Observable<SignedUrlResponse>
   */
  public getPreviewFileUrl(url: string): Observable<SignedUrlResponse> {
    const requestPreviewUrl: string = `${url}?action=${API_QUERY_PARAMS.SINGLE_FILE_DOWNLOAD_ACTION_VALUE}`;
    return this.backendService.commonApiGetReq<SignedUrlResponse>(requestPreviewUrl);
  }

  /**
   * @description Get details of public file.
   *
   * @param fileId public file id
   * @return Observable<SignedUrlResponse>
   */
   public getPublicFileDetails(fileId: string): Observable<SignedUrlResponse> {
    const publicFileUrl: string = `/mithoo/shared-files/${fileId}?action=${API_QUERY_PARAMS.SINGLE_FILE_DOWNLOAD_ACTION_VALUE}`;
    return this.commonApiService.get(publicFileUrl);
  }

  /**
   * @description Toggle public/private sharing of file
   *
   * @param url link.self url from api response
   * @param accessType public or private
   * @return Observable<SignedUrlResponse>
   */
  public toggleShare(url: string, accessType: string): Observable<PublicShareResponse> {
    const requestPreviewUrl: string = `${url}?action=${API_QUERY_PARAMS.FILE_SHARE_ACTION_VALUE}&access_type=${accessType}`;
    return this.backendService.commonApiGetReq<PublicShareResponse>(requestPreviewUrl);
  }

  public downloadDataset(repoID: string, datasetID: string): Observable<DatasetDownloadResponse> {
    return this.dataService.downloadDataset(repoID, datasetID);
  }

  /**
   * @description Downloads the HTML Report file along with its dependencies in .zip format
   *
   * @param url Download link for file
   * @param token JWT Token used for authentication
   * @return Observable
   */
  public downloadReportFile(url: string, token: string): Observable<object> {
    const websocketConnectionId: string = encodeURIComponent(this.websocketService.getConnectionId() || '');
    url = `${url}/filepath/?jwt_token=${token}&request_type=zip_download&conn_id=${websocketConnectionId}`;
    return this.http.get(url);
  }

  public downloadPlotlyPlot(divId: string, format: PlotlyImageFormat = 'png', filename?: string): void {
    const divRef = this.plotlyService.getInstanceByDivId(divId);
    this.plotlyService.getPlotly().then((instance) => {
      instance.downloadImage(divRef, {
        format: format,
        filename: filename || `plotly_image.${format}`,
      });
    });
  }

  public downloadElasticQueryResponse(
    term: StringToStringObjectMapping,
    expectedColumns: string[],
    indexName: string,
    isGDX: boolean,
    sortParams: ElasticSortingQuery[],
    fileName: string
  ): Promise<void> {
    this.messageService.showToast(
      'Download initiated',
      'CSV creation started for your gene list file. It will be downloaded shortly upon completion.',
      'info'
    );
    const query: object = getDownloadQuery(term);
    const downloadPayload = getElasticQueryDownloadPayload(expectedColumns, indexName, isGDX, sortParams, query, fileName);
    return new Promise((resolve) => {
      lastValueFrom(this.dataService.createDownloadElasticQuery(downloadPayload)).then((createQueryResponse: ProcessedDataQueryResponse) => {
        const { data: { id } } = createQueryResponse;
        let apiCallsMadeCount = 0;
        const timer = setInterval(() => {
          if (apiCallsMadeCount > 10) {
            clearInterval(timer);
            resolve();
          }
          lastValueFrom(this.dataService.checkDownloadElasticQueryStatus(id)).then((queryStatus: ProcessedDataQueryResponse) => {
            apiCallsMadeCount += 1;
            const status = queryStatus.data.attributes.status;
            if (status === 'COMPLETED') {
              clearInterval(timer);
              lastValueFrom(this.dataService.getElasticDownloadSignedURL(id)).then((downloadQueryResponse: QueryDownloadFileResponse) => {
                if (downloadQueryResponse.data) {
                  lastValueFrom(this.downloadStream(downloadQueryResponse.data.download_url, fileName));
                }
                resolve();
              })
            }
            else if (status === 'FAILED') {
              clearInterval(timer);
              this.messageService.showToast('Could not download', 'Some Error occurred', 'error');
              resolve();
            }
          });
        }, 1500);

      }).catch(() => {
        this.messageService.showToast('Could not download', 'Some Error occurred', 'error');
        resolve();
      });

    });
  }
}
